import * as THREE from 'three';
import { DSTEEL, basic, emis, glowMat, std, stone } from '../materials.ts';
import { G, M, V, flame, glowSprite, limb, type Flame } from '../prims.ts';
import { mulberry32, type Rng } from '../rand.ts';
import { jackOLantern, RAG, RIND, RIND_DARK, VINE, type Lantern } from '../models/seasonal.ts';
import type { Model } from '../models/types.ts';
import type { EnemyDresser } from './types.ts';

/*
 * Every nightmare dressed for the Night of Nightmares, baked as its own variant: goblins under carved
 * pumpkin helmets, skeletons with a candle burning on the skull, wraiths in torn witch hats with a
 * grave-green glow, wolves with ember eyes and ragged fur, gargoyles on bat wings, slimes gone sickly
 * orange, stalkers cloaked with a lantern, sappers with a pumpkin lantern, bone rams lit with candles and
 * spectral archers with arrows of fire. Silhouettes stay readable at game zoom.
 */

const inner = (m: Model): THREE.Object3D => m.root.getObjectByName('model') ?? m.root;

/** Runs `f` after the model's own tick (flicker for the candles and lanterns added here). */
const after = (m: Model, f: (n: number) => void): void => {
  const t0 = m.tick;
  m.tick = (n) => {
    t0(n);
    f(n);
  };
};

/** Recolors a model's glowing bits (eyes, wisps): sprites and self-lit meshes. */
const reglow = (root: THREE.Object3D, color: string): void => {
  const em = emis('#fff4e0', color, 1.6);
  root.traverse((o) => {
    if ((o as THREE.Sprite).isSprite) {
      const s = o as THREE.Sprite;
      s.material = glowMat(color, s.material.opacity);
      return;
    }
    const mesh = o as THREE.Mesh;
    const mat = mesh.material as THREE.MeshStandardMaterial | undefined;
    if (mesh.isMesh && mat?.isMeshStandardMaterial && mat.emissiveIntensity > 0.2 && mat.emissive.getHex() !== 0) mesh.material = em;
  });
};

/** A cape hung behind from `top`, torn into strips at the hem; `len` long, `r` wide at the hem. */
const cape = (parent: THREE.Object3D, top: THREE.Vector3, len: number, r: number, mat: THREE.Material, rng: Rng, tilt = 0.12): THREE.Mesh => {
  const geo = new THREE.ConeGeometry(r, len, 12, 4, true, Math.PI / 2, Math.PI);
  const p = geo.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < p.count; i++) {
    const y = p.getY(i);
    if (y < -len * 0.3) p.setY(i, y + (i % 2 ? len * 0.18 : rng() * len * 0.12));
  }
  geo.computeVertexNormals();
  const m = M(geo, mat, parent);
  m.position.set(top.x, top.y - len / 2, top.z);
  m.rotation.x = tilt;
  return m;
};

/** A dripping candle with a flame on top; returns the flame for flicker. */
const candle = (parent: THREE.Object3D, pos: THREE.Vector3, h: number, rng: Rng, s = 0.3): Flame => {
  const wax = std('#e3dac4', { roughness: 0.6 });
  const c = M(new THREE.CylinderGeometry(0.028, 0.032, h, 7), wax, parent);
  c.position.set(pos.x, pos.y + h / 2, pos.z);
  for (let i = 0; i < 3; i++) {
    const d = M(new THREE.SphereGeometry(0.012, 5, 4), wax, parent);
    d.scale.y = 2.2;
    const a = rng() * Math.PI * 2;
    d.position.set(pos.x + Math.cos(a) * 0.03, pos.y + h * (0.55 + rng() * 0.35), pos.z + Math.sin(a) * 0.03);
  }
  return flame(parent, V(pos.x, pos.y + h, pos.z), s, rng, '#ff9a3a');
};

/** A pointed witch hat, a little crooked, with a torn brim. Base at the origin of the returned group. */
const witchHat = (parent: THREE.Object3D, pos: THREE.Vector3, size: number, rng: Rng, band: THREE.Material): THREE.Group => {
  const hat = G(parent, pos.x, pos.y, pos.z);
  const felt = std('#141018', { roughness: 0.95, side: THREE.DoubleSide });
  const brim = new THREE.RingGeometry(0.06 * size, 0.32 * size, 18, 1);
  const bp = brim.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < bp.count; i++) {
    const x = bp.getX(i);
    const y = bp.getY(i);
    const r = Math.hypot(x, y);
    if (r > 0.2 * size) {
      const k = 1 - (i % 4 === 0 ? 0.22 : rng() * 0.1);
      bp.setXYZ(i, x * k, y * k, (rng() - 0.3) * 0.04 * size);
    }
  }
  brim.computeVertexNormals();
  M(brim, felt, hat).rotation.x = -Math.PI / 2;
  const cone = M(new THREE.ConeGeometry(0.15 * size, 0.36 * size, 10, 1, true), felt, hat);
  cone.position.y = 0.18 * size;
  // The crumpled tip, bent back.
  const tip = G(hat, 0, 0.34 * size, 0);
  tip.rotation.x = -0.9;
  const t = M(new THREE.ConeGeometry(0.06 * size, 0.24 * size, 8, 1, true), felt, tip);
  t.position.y = 0.1 * size;
  ring(hat, 0.13 * size, 0.012 * size, band, 0.03 * size);
  return hat;
};

const ring = (parent: THREE.Object3D, r: number, tube: number, mat: THREE.Material, y: number): void => {
  const m = M(new THREE.TorusGeometry(r, tube, 4, 18), mat, parent);
  m.rotation.x = Math.PI / 2;
  m.position.y = y;
};

/** A small iron lantern with a candle-lit core; hangs from its ring at the origin of the group. */
const ironLantern = (parent: THREE.Object3D, pos: THREE.Vector3, s: number): { g: THREE.Group; glow: THREE.Sprite } => {
  const g = G(parent, pos.x, pos.y, pos.z);
  const core = emis('#ffe0a0', '#ff8a1a', 2.4);
  M(new THREE.BoxGeometry(0.07 * s, 0.09 * s, 0.07 * s), core, g).position.y = -0.08 * s;
  for (const [x, z] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) M(new THREE.BoxGeometry(0.012 * s, 0.11 * s, 0.012 * s), DSTEEL, g).position.set(x * 0.04 * s, -0.08 * s, z * 0.04 * s);
  M(new THREE.ConeGeometry(0.06 * s, 0.05 * s, 4), DSTEEL, g).position.y = -0.01 * s;
  M(new THREE.BoxGeometry(0.08 * s, 0.012 * s, 0.08 * s), DSTEEL, g).position.y = -0.135 * s;
  const glow = glowSprite(g, '#ff8a1a', 0.55 * s, V(0, -0.08 * s, 0), 0.85, true);
  return { g, glow };
};

const rindFor = (tier: number): THREE.Material => (tier <= 1 ? RIND : tier === 2 ? RIND_DARK : std('#3d2a20', { roughness: 0.9, flatShading: true }));

export const dressHalloween: EnemyDresser = (kind, m, o) => {
  const g = inner(m);
  const rng = mulberry32(0x0a11 + kind.length * 31 + o.tier * 7);
  const lanterns: Lantern[] = [];
  const flames: Flame[] = [];
  const flick = (): void => {
    for (const l of lanterns) l.flick(rng);
    for (const f of flames) f.flick();
  };
  switch (kind) {
    case 'goblin': {
      // A carved pumpkin pulled over the head, ears poking out; a ragged cape on the back.
      const style = ((o.tier - 1) % 3) as 0 | 1 | 2;
      lanterns.push(jackOLantern(g, V(0, 0.95, 0.2), 0.205, style, rindFor(o.tier), 2.6, RAG));
      cape(g, V(0, 1.02, -0.08), o.elite ? 0.8 : 0.55, o.elite ? 0.36 : 0.26, RAG, rng, 0.25);
      if (o.elite) {
        // The warlord's cape is longer and a lantern swings from his belt.
        const l = ironLantern(g, V(-0.26, 0.62, 0.12), 1.2);
        after(m, () => {
          l.glow.material.opacity = 0.55 + rng() * 0.4;
        });
      }
      break;
    }
    case 'skeleton': {
      // A tattered hood and a stub of candle burning on the crown of the skull.
      // Open at the front (three's sphere puts +Z at phi = π/2), so the skull grins out of it.
      const hood = M(new THREE.SphereGeometry(0.155, 12, 8, Math.PI / 2 + 1.0, Math.PI * 2 - 2.0, 0, Math.PI / 1.45), RAG, g);
      hood.position.set(0, 1.63, -0.02);
      hood.rotation.x = -0.2;
      cape(g, V(0, 1.46, -0.04), 0.82, 0.3, RAG, rng, 0.1);
      flames.push(candle(g, V(0.01, 1.74, 0.0), 0.11, rng, 0.32));
      break;
    }
    case 'wraith': {
      // A torn witch hat over the hood, and a grave-green glow in place of the pale one.
      reglow(g, '#9dffb0');
      witchHat(g, V(0, 1.74, 0.0), 1.05, rng, emis('#6aff8a', '#2fd96a', 1.1)).rotation.set(-0.2, 0, 0.12);
      glowSprite(g, '#5aff8a', 0.9, V(0, 1.15, 0.05), 0.28);
      // Green threads of corpse-light trailing from the hem.
      const wm = basic('#5aff8a', 0.25);
      for (let i = 0; i < 3; i++) {
        const a = i * 2.1;
        const l = limb(g, V(Math.cos(a) * 0.2, 0.45, Math.sin(a) * 0.2), V(Math.cos(a) * 0.32, 0.05, Math.sin(a) * 0.32), 0.02, 0.004, wm, 4);
        l.renderOrder = 1;
      }
      break;
    }
    case 'wolf': {
      // Ember eyes and a ragged ridge of fur standing up along the spine.
      reglow(g, '#ff7a1a');
      const fur = std('#0e0b14', { roughness: 1, flatShading: true });
      for (let i = 0; i < 9; i++) {
        const z = 0.42 - i * 0.1;
        const sp = M(new THREE.ConeGeometry(0.035 + (i < 3 ? 0.015 : 0), 0.16 + rng() * 0.06, 4), fur, g);
        sp.position.set((rng() - 0.5) * 0.05, 0.76 - Math.abs(z - 0.05) * 0.18, z);
        sp.rotation.set(-0.9 - rng() * 0.3, 0, (rng() - 0.5) * 0.5);
      }
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        const sp = M(new THREE.ConeGeometry(0.04, 0.16, 4), fur, g);
        sp.position.set(Math.cos(a) * 0.2, 0.66 + Math.sin(a) * 0.12, 0.4);
        sp.rotation.set(-0.6, 0, -a + Math.PI / 2);
      }
      break;
    }
    case 'gargoyle': {
      // Bat wings: leathery membranes stretched over finger bones, riding on the stone wings.
      const skin = std('#2b1d2c', { roughness: 0.8, side: THREE.DoubleSide });
      g.traverse((ob) => {
        const w = ob as THREE.Mesh;
        if (!w.isMesh || w.geometry.type !== 'ExtrudeGeometry' || w.userData.membrane) return;
        const s = new THREE.Shape();
        s.moveTo(0, 0.05);
        s.lineTo(0.85, 0.5);
        s.quadraticCurveTo(0.8, 0.2, 0.86, -0.02);
        s.quadraticCurveTo(0.68, 0.06, 0.6, -0.12);
        s.quadraticCurveTo(0.45, -0.02, 0.36, -0.2);
        s.quadraticCurveTo(0.22, -0.06, 0.08, -0.16);
        s.closePath();
        const mem = M(new THREE.ShapeGeometry(s, 3), skin, w);
        mem.position.z = 0.012;
        mem.userData.membrane = true;
        for (const tip of [V(0.86, -0.02, 0.01), V(0.6, -0.12, 0.01), V(0.36, -0.2, 0.01)]) limb(w, V(0.02, 0.04, 0.01), tip, 0.012, 0.005, stone('#4f4c48'), 4);
      });
      break;
    }
    case 'slime': {
      // Sickly orange ooze with a violet heart.
      const ooze = std('#c0561a', { roughness: 0.08, metalness: 0.1, transparent: true, opacity: 0.74, bumpScale: 0 });
      g.traverse((ob) => {
        const mesh = ob as THREE.Mesh;
        const mat = mesh.material as THREE.MeshStandardMaterial | undefined;
        if (mesh.isMesh && mat?.transparent && mesh.geometry.type === 'IcosahedronGeometry') mesh.material = ooze;
      });
      glowSprite(g, '#9b4dff', 1.0, V(0, 0.38, 0), 0.45);
      const bub = emis('#d9b8ff', '#9b4dff', 1.6);
      for (let i = 0; i < 4; i++) M(new THREE.SphereGeometry(0.03 + rng() * 0.02, 6, 5), bub, g).position.set((rng() - 0.5) * 0.5, 0.3 + rng() * 0.3, (rng() - 0.5) * 0.4);
      break;
    }
    case 'stalker': {
      // A long cloak over the hunched back, and a lantern dragged along low in one claw.
      const cl = cape(g, V(0, 0.98, 0.18), 0.85, 0.34, RAG, rng, -0.95);
      cl.position.z -= 0.1;
      const l = ironLantern(g, V(-0.32, 0.26, 0.62), 1.3);
      limb(g, V(-0.3, 0.16, 0.56), V(-0.32, 0.26, 0.62), 0.006, 0.006, DSTEEL, 4);
      after(m, (n) => {
        l.glow.material.opacity = 0.5 + rng() * 0.45;
        l.g.rotation.z = n % 4 < 2 ? 0.2 : -0.2;
      });
      break;
    }
    case 'sapper': {
      // A carved pumpkin hung from the belt and the powder sack's fuse well alight.
      lanterns.push(jackOLantern(g, V(0.23, 0.3, 0.1), 0.085, 1, RIND, 2.2, RAG));
      limb(g, V(0.22, 0.48, 0.1), V(0.23, 0.44, 0.1), 0.008, 0.008, DSTEEL, 4);
      flames.push(flame(g, V(-0.17, 1.0, -0.15), 0.2, rng, '#ff9a3a'));
      break;
    }
    case 'ram': {
      // Ember eyes carved bright, candles guttering along its spine and a jack-o'-lantern riding on the hide.
      reglow(g, '#ff7a1a');
      for (let i = 0; i < 4; i++) flames.push(candle(g, V(i % 2 ? 0.07 : -0.07, 1.04, -0.75 + i * 0.36), 0.1 + rng() * 0.06, rng, 0.3));
      const jl = jackOLantern(g, V(0, 1.0, -0.95), 0.16, 2, RIND, 2.6);
      jl.group.rotation.y = Math.PI;
      lanterns.push(jl);
      break;
    }
    case 'mangonel': {
      // Ember eyes, candles guttering on the uprights and a jack-o'-lantern grinning from the prow.
      reglow(g, '#ff7a1a');
      for (const k of [-1, 1]) flames.push(candle(g, V(k * 0.3, 1.32, 0.03), 0.1 + rng() * 0.05, rng, 0.3));
      lanterns.push(jackOLantern(g, V(0, 0.62, 0.72), 0.13, 2, RIND, 2.4));
      break;
    }
    case 'mammoth': {
      // Ember eyes and seams gone pumpkin-orange, candles guttering on the wrecked howdah and a jack-o'-lantern
      // riding among its burnt boards.
      reglow(g, '#ff7a1a');
      for (const [x, y, z] of [[-0.32, 2.82, -0.2], [0.24, 2.83, -0.08], [0.3, 2.74, -0.72]] as const) flames.push(candle(g, V(x, y, z), 0.12 + rng() * 0.06, rng, 0.35));
      lanterns.push(jackOLantern(g, V(-0.05, 2.97, -0.42), 0.22, 2, RIND, 3));
      break;
    }
    case 'archer': {
      // Arrows of fire instead of cold light, more burning in the quiver, and a ragged cape over the robe.
      reglow(g, '#ff7a1a');
      for (const [x, z] of [[0.19, -0.22], [0.23, -0.27]] as const) flames.push(flame(g, V(x, 1.6, z), 0.2, rng));
      cape(g, V(0, 1.5, -0.06), 1.0, 0.34, RAG, rng, 0.12);
      break;
    }
    case 'boneWall': {
      // Candles stuck among the ribs, guttering.
      for (const [x, z] of [[-0.32, 0.12], [-0.05, -0.1], [0.22, 0.1], [0.4, -0.08]] as const) flames.push(candle(g, V(x, 0.14, z), 0.1 + rng() * 0.08, rng, 0.3));
      break;
    }
    case 'witch': {
      // The Ash Witch in her tall crooked hat, candles floating about her.
      witchHat(g, V(0, 1.74, 0.18), 1.5, rng, emis('#9dff6a', '#4fd93a', 1.0)).rotation.set(-0.25, 0, -0.15);
      const ring4: THREE.Group[] = [];
      for (let i = 0; i < 4; i++) {
        const cg = G(g);
        flames.push(candle(cg, V(0, 0, 0), 0.12, rng, 0.3));
        ring4.push(cg);
      }
      after(m, (n) => {
        ring4.forEach((cg, i) => {
          const a = n * 0.35 + (i / 4) * Math.PI * 2;
          cg.position.set(Math.cos(a) * 0.55, 1.0 + i * 0.18 + (n % 4 < 2 ? 0.03 : 0), Math.sin(a) * 0.55);
        });
      });
      break;
    }
    case 'shell': {
      // Jack-o'-lanterns grown on its back out of a tangle of vines.
      const vine = VINE;
      for (let i = 0; i < 5; i++) {
        const a = i * 1.26;
        const pts: THREE.Vector3[] = [];
        for (let j = 0; j <= 5; j++) {
          const b = (j / 5) * 1.25;
          pts.push(V(Math.cos(a + j * 0.25) * Math.sin(b) * 0.65, 0.62 + Math.cos(b) * 0.5, Math.sin(a + j * 0.25) * Math.sin(b) * 0.65));
        }
        M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 10, 0.025, 5), vine, g);
      }
      lanterns.push(jackOLantern(g, V(0, 1.02, 0), 0.24, 1, RIND, 3));
      for (let i = 0; i < 3; i++) {
        const a = 0.5 + i * 2.1;
        const p = jackOLantern(g, V(Math.cos(a) * 0.38, 0.93, Math.sin(a) * 0.38), 0.14, (i % 3) as 0 | 1 | 2, i === 1 ? RIND_DARK : RIND, 3);
        p.group.rotation.y = Math.atan2(Math.cos(a), Math.sin(a));
        lanterns.push(p);
      }
      break;
    }
    case 'warden':
      // The crypt's warden: candles burning along his slab of bone.
      for (let i = 0; i < 3; i++) flames.push(candle(g, V(-0.25 + i * 0.25, 1.75 + rng() * 0.1, 0.3), 0.12, rng, 0.35));
      break;
    default:
      break;
  }
  if (lanterns.length > 0 || flames.length > 0) after(m, flick);
  flick();
};
